import api from './api';

type TodayDataV1 = {
  version: 1;
  items: any[];
  days: Record<string, any>;
};

type Guide = {
  id: string;
  slug: string;
  title: string;
  description?: string | null;
  content: string;
  pinned?: boolean;
  sortOrder?: number;
  updatedAt?: string;
};

const TODAY_KEY = 'personal_blog.today.v1';
const LOCAL_GUIDES_KEY = 'personal_blog.guides.local.v1';
const MIGRATED_KEY_PREFIX = 'personal_blog.frontend.migrated_to_cloud.v1.user';

function safeJsonParse<T>(s: string | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

function hasMeaningfulToday(d: TodayDataV1) {
  const hasItems = Array.isArray(d.items) && d.items.length > 0;
  const hasAnyDone = Object.values(d.days ?? {}).some((s: any) => (s?.doneIds?.length ?? 0) > 0);
  return hasItems || hasAnyDone;
}

export type MigrationResult = {
  today: 'skipped' | 'pushed' | 'failed';
  guidesCreated: number;
  guidesUpdated: number;
  guidesFailed: number;
};

export function hasMigrated(userId: number) {
  return localStorage.getItem(`${MIGRATED_KEY_PREFIX}.${userId}`) === '1';
}

export function markMigrated(userId: number) {
  localStorage.setItem(`${MIGRATED_KEY_PREFIX}.${userId}`, '1');
}

export async function migrateGuestDataToCloud(userId: number): Promise<MigrationResult> {
  const result: MigrationResult = { today: 'skipped', guidesCreated: 0, guidesUpdated: 0, guidesFailed: 0 };

  // 1) Today 数据：云端为空则推送本地
  const localToday = safeJsonParse<TodayDataV1>(localStorage.getItem(TODAY_KEY));
  if (localToday && localToday.version === 1 && hasMeaningfulToday(localToday)) {
    try {
      const cloud = await api.get<{ data: TodayDataV1 | null }>('/today-data');
      const cloudEmpty = !cloud.data.data || !hasMeaningfulToday(cloud.data.data);
      if (cloudEmpty) {
        await api.put('/today-data', { data: localToday });
        result.today = 'pushed';
      }
    } catch (e: any) {
      if (e?.response?.status === 404) {
        result.today = 'failed';
      } else {
        result.today = 'failed';
      }
      result.today = 'failed';
    }
  }

  // 2) Guides：把本地原则导入/合并到云端（按 slug upsert）
  const localGuides = safeJsonParse<Guide[]>(localStorage.getItem(LOCAL_GUIDES_KEY)) ?? [];
  if (Array.isArray(localGuides) && localGuides.length) {
    try {
      const cloudList = await api.get<Guide[]>('/user-guides');
      const bySlug = new Map<string, Guide>();
      for (const g of cloudList.data ?? []) if (g?.slug) bySlug.set(g.slug, g);

      for (const g of localGuides) {
        if (!g?.slug || !g?.title || !g?.content) continue;
        const existing = bySlug.get(g.slug);
        try {
          if (existing?.id) {
            await api.put(`/user-guides/${encodeURIComponent(existing.id)}`, {
              title: g.title,
              slug: g.slug,
              description: g.description ?? null,
              content: g.content,
              pinned: !!g.pinned,
              sortOrder: g.sortOrder ?? 0,
            });
            result.guidesUpdated += 1;
          } else {
            await api.post('/user-guides', {
              title: g.title,
              slug: g.slug,
              description: g.description ?? null,
              content: g.content,
              pinned: !!g.pinned,
              sortOrder: g.sortOrder ?? 0,
            });
            result.guidesCreated += 1;
          }
        } catch {
          result.guidesFailed += 1;
        }
      }
    } catch (e: any) {
      // cloud list 失败时：整体算失败，但不抛，交给 UI 提示
      result.guidesFailed = Math.max(result.guidesFailed, 1);
    }
  }

  // userId 仅用于 key；eslint/ts 需要使用
  void userId;
  return result;
}

